/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPercentages,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const baseStyle: Interpolation<ThemeType> = (theme) => ({
  alignContent: "center",
  backgroundColor: theme.semanticColors.accentLowest,
  borderRadius: radius.ms,
  color: theme.semanticColors.accentHigher,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  marginTop: numericPixels[15],
  minWidth: spacing.none,
  paddingBottom: spacing.xl,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  position: "relative",
  textAlign: "center",
  width: spacing.fit,
  [cssMediaQueryMinWidthTablet]: { minWidth: numericPixels[300] },
});
const basePaddingTopStyle: Interpolation = {
  paddingTop: spacing.l,
};
const basePaddingTopHeadlineStyle: Interpolation = {
  paddingTop: spacing.xl,
};
const headlineStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.accentHigh,
  borderRadius: numericPixels[5],
  color: theme.semanticColors.accentLowest,
  display: "flex",
  height: numericPixels[30],
  left: numericPercentages[50],
  paddingLeft: numericPixels[10],
  paddingRight: numericPixels[10],
  position: "absolute",
  textTransform: "uppercase",
  top: spacing.none,
  transform: `translate(-50%, -50%)`,
});

export type PromotionalOfferProps = WithAsChild<
  Omit<HallowElementProps<typeof hallowElement>, "children">
> & {
  description?: string;
  headline?: string;
  title?: string;
};

export const PromotionalOffer = forwardRef<any, PromotionalOfferProps>(
  ({ asChild = false, description, headline, title, ...props }, ref) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[
          baseStyle,
          headline ? basePaddingTopHeadlineStyle : basePaddingTopStyle,
        ]}
        {...props}
      >
        {headline && (
          <Text size="xs" type="title" css={headlineStyle}>
            {headline}
          </Text>
        )}
        <Text size="s" type="headline">
          {title}
        </Text>
        <Text size="xl" type="detail">
          {description}
        </Text>
      </DeterminedElement>
    );
  },
);

PromotionalOffer.displayName = "PromotionalOffer";
